import {MetricSpecifier} from "./MultiMetoroMetricsCharts";
import {ReduceOption, StatWidgetMapping} from "../Dashboarding/internalwidgets";
import {useEffect, useState} from "react";
import {
    createMultiMetricRequest,
    GetAggregateMultiMetricEvaluation,
    MetoroQLQuerySpecifier
} from "../../clients/metoro/metrics";
import {Textfit} from "react-textfit";
import {WindowUnit} from "../../pages/alerts/MetricAlert";
import {useSelector} from "react-redux";
import timerange from "../../store/reducers/timerange";
import {cn} from "../ui/lib/utils";
import {LoadingSpinner} from "../ui/customSpinner";
import {RuntimeVariable} from "../Dashboarding/Dashboard";
import {substituteVariablesInFilters} from "./utils";
import {GridStack} from "gridstack";
import {useSearchParams} from "react-router-dom";
import {Formula} from "../../types/formula";
import {ColumnConfig, DataTable} from "../ui/datatable/datatable";
import {Column, ColumnDef} from "@tanstack/react-table";
import {DataTableColumnHeader} from "../ui/datatable/datatablecolumnheader";
import {Tooltip, TooltipContent, TooltipTrigger} from "../ui/tooltip";
import { formatValueWithUnit as formatValueWithUnitConverter } from "../../utility/unitConverter";

// Helper function to evaluate if a value matches a mapping
function doesValueMatchMapping(value: number, mapping: StatWidgetMapping): boolean {
    if (mapping.operator === "range") {
        return value >= (mapping.from ?? -Infinity) && value <= (mapping.to ?? Infinity);
    }

    switch (mapping.operator) {
        case ">":
            return value > mapping.value;
        case "<":
            return value < mapping.value;
        case "==":
            return value === mapping.value;
        case ">=":
            return value >= mapping.value;
        case "<=":
            return value <= mapping.value;
        default:
            return false;
    }
}

// Find the first matching mapping for a value
function findMatchingMapping(value: number, mappings: StatWidgetMapping[] | undefined): StatWidgetMapping | undefined {
    if (!mappings) return undefined;

    // First check exact matches
    const exactMatch = mappings.find(m => m.operator === "==" && m.value === value);
    if (exactMatch) return exactMatch;

    // Then check ranges and other operators
    return mappings.find(m => doesValueMatchMapping(value, m));
}

export interface StatValue {
    attributes: { [key: string]: string };
    value: number | undefined;
    unit?: string;
}

interface StatTableRow extends StatValue {
    id: string;
}

export function getMetoroQLQueryStartEndTime(metoroQLQuery: MetoroQLQuerySpecifier, startTime: number, endTime: number): [number, number] {
    const bucketSize = metoroQLQuery.bucketSize!
    const numBuckets = Math.floor((endTime - startTime) / bucketSize)
    const newEndTime = endTime - (endTime % bucketSize)
    const newStartTime = newEndTime - (numBuckets * bucketSize)
    return [newStartTime, newEndTime]
}

export function getAggregateStartEndTime(metricSpecifiers: MetricSpecifier[], startTime: number, endTime: number): [number, number] {
    // Get the time bucket from the first metric specifier
    if (metricSpecifiers.length < 1) {
        return [startTime, endTime]
    }
    // We need to get align the start and end time with the buckets we have
    // For example if we have 12:12 -> 12:17 with 5 minute buckets then we should just
    // have 1 bucket going from 12:10 -> 12:15
    // If we have 12:02 -> 12:17 then we should have 3 buckets:
    // 12:00 -> 12:05
    // 12:05 -> 12:10
    // 12:10 -> 12:15
    // If we have 12:00 -> 12:19 with 5 minute buckets then we should have the same
    // We round down

    const bucketSize = metricSpecifiers[0].bucketSize || 60; // Default to 60 seconds if not specified

    // First figure out how many buckets we have
    const numBuckets = Math.floor((endTime - startTime) / bucketSize)

    // Truncate the endtime
    const newEndTime = endTime - (endTime % bucketSize)
    const newStartTime = newEndTime - (numBuckets * bucketSize)

    return [newStartTime, newEndTime]
}

let lastRenderErrorTime = 0;

export function StatChart(props: {
    title?: string
    setTitle?: (title: string) => void
    metricSpecifiers: MetricSpecifier[]
    formulas?: Formula[]
    metoroQLQueries?: MetoroQLQuerySpecifier[]
    reduceOption: ReduceOption
    mappings?: StatWidgetMapping[]
    className?: string
    precision?: number
    fontSize?: string
    runtimeVariables?: RuntimeVariable[]
    grids?: Map<string, GridStack>
    widgetId?: string
    setIsTable?: (isTable: boolean) => void
    removeBorder?: boolean
    ignoreEnvironment?: boolean
    startTime?: number
    endTime?: number,
    isEditable?: boolean
    columnConfigs?: ColumnConfig[],
    setColumnConfigs?: React.Dispatch<React.SetStateAction<ColumnConfig[]>>,
    isSelecting?: boolean
    unitOverride?: string  // Optional unit override for the displayed value
}) {
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const [statValues, setStatValues] = useState<StatValue[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchParams] = useSearchParams();
    const [environments, setEnvironments] = useState<string[]>([]);
    const precision = props.precision ?? 2;
    const [isTable, setIsTable] = useState<boolean>(false);
    const [singleValueToRender, setSingleValueToRender] = useState<string>("value");
    const [updateMetricsAbortController, setUpdateMetricsAbortController] = useState<AbortController>(new AbortController());
    const [metricUnit, setMetricUnit] = useState<string>("");

    // Used to make sure that the fitting of the text happens after the component is mounted
    useEffect(() => {
        // dispatch it once mounted
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 200);
    }, []);

    useEffect(() => {
        if (props.ignoreEnvironment) return;
        let environment = searchParams.get("environment");
        if (environment !== null && environment !== "") {
            setEnvironments([environment]);
        } else {
            setEnvironments([]);
        }
    }, [searchParams]);

    const getVisualStyles = (value: number | undefined) => {
        if (value == undefined || value == null) {
            return {color: "#C6D3E2"}
        }
        if (!props.mappings) return {color: "#C6D3E2"};

        const matchingMapping = findMatchingMapping(value, props.mappings);
        if (matchingMapping) {
            return matchingMapping.type === "background"
                ? {backgroundColor: matchingMapping.color, padding: "0.5rem", borderRadius: "0.25rem"}
                : {color: matchingMapping.color};
        }

        return {color: "#C6D3E2"};
    };

    const formatValue = (value: number | undefined, unit?: string) => {
        if (value == undefined || value == null) {
            return "-"
        }

        // Use unitOverride if provided, otherwise use the unit from the metric
        const unitToUse = props.unitOverride || unit;

        // Use the unit converter if we have a unit
        let formatted: string;
        if (unitToUse && unitToUse.trim() !== '') {
            // For timestamps, pass formatting options similar to chart axes
            if (unitToUse.startsWith('unix_')) {
                formatted = formatValueWithUnitConverter(value, unitToUse, precision, {
                    shortFormat: false, // Use full format for stat values
                    includeTime: true,
                    includeMillis: unitToUse === 'unix_ms' || unitToUse === 'unix_us' || unitToUse === 'unix_ns'
                });
            } else {
                formatted = formatValueWithUnitConverter(value, unitToUse, precision);
            }
        } else {
            // Fallback to unit converter for unitless values - it handles them properly now
            formatted = formatValueWithUnitConverter(value, '', precision);
        }

        if (!props.mappings) return formatted;

        const matchingMapping = findMatchingMapping(value, props.mappings);
        if (matchingMapping && matchingMapping.textMapping) {
            return matchingMapping.textMapping.replace('$value', formatted);
        }
        return formatted;
    };


    const calculateWindowParams = (startTime: number, endTime: number): {
        windowSize: number,
        windowUnit: WindowUnit
    } => {
        const timeRangeInSeconds = endTime - startTime;
        const timeRangeInMinutes = timeRangeInSeconds / 60;
        const timeRangeInHours = timeRangeInMinutes / 60;
        const timeRangeInDays = timeRangeInHours / 24;

        if (timeRangeInDays > 7) {
            // For ranges > 7 days, use days
            return {
                windowSize: Math.max(1, Math.floor(timeRangeInDays / 60)),
                windowUnit: WindowUnit.Days
            };
        } else if (timeRangeInHours > 24) {
            // For ranges > 24 hours, use hours
            return {
                windowSize: Math.max(1, Math.floor(timeRangeInHours / 60)),
                windowUnit: WindowUnit.Hours
            };
        } else {
            // For ranges <= 24 hours, use minutes
            return {
                windowSize: Math.max(1, Math.floor(timeRangeInMinutes / 60)),
                windowUnit: WindowUnit.Minutes
            };
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                let startTime = props.startTime || Math.floor(timeRange.getStartEnd()[0].getTime() / 1000);
                let endTime = props.endTime || Math.floor(timeRange.getStartEnd()[1].getTime() / 1000);

                let res = getAggregateStartEndTime(props.metricSpecifiers, startTime, endTime)
                startTime = res[0]
                endTime = res[1]


                const {windowSize, windowUnit} = calculateWindowParams(startTime, endTime);

                const processedMetricSpecifiers = props.metricSpecifiers.map(spec => {
                    const processedFilters = substituteVariablesInFilters(spec.filters || new Map(), props.runtimeVariables);
                    const processedExcludeFilters = substituteVariablesInFilters(spec.excludeFilters || new Map(), props.runtimeVariables);

                    if (environments.length > 0 && !processedFilters.has("environment")) {
                        processedFilters.set("environment", environments);
                    }

                    return {
                        ...spec,
                        filters: processedFilters,
                        excludeFilters: processedExcludeFilters,
                    };
                });

                const multiMetricRequest = createMultiMetricRequest(
                    processedMetricSpecifiers,
                    startTime,
                    endTime,
                    true,
                    props.formulas,
                    props.runtimeVariables,
                    props.metoroQLQueries,
                    environments,
                );

                const aggregateParams = {
                    evaluationFunction: props.reduceOption,
                    window: windowSize,
                    windowUnit: windowUnit,
                    evaluationSplits: processedMetricSpecifiers[0]?.splits || [],
                };

                const response = await GetAggregateMultiMetricEvaluation(multiMetricRequest, aggregateParams, updateMetricsAbortController, setUpdateMetricsAbortController);
                if (response.metric.timeSeries && response.metric.timeSeries.length > 0) {
                    // Store the unit from the metric response
                    setMetricUnit(response.metric.unit || "");

                    const values: StatValue[] = response.metric.timeSeries.map(series => {
                        return {
                            attributes: typeof series.attributes === 'object' ?
                                (series.attributes instanceof Map ?
                                    Object.fromEntries(series.attributes) :
                                    series.attributes) :
                                {},
                            value: series.data && series.data.length > 0 ? series.data[0].value : 0,
                            unit: response.metric.unit || ""
                        };
                    });
                    setStatValues(values);
                } else {
                    setStatValues([]);
                    setMetricUnit("");
                }
            } catch (error) {
                console.error("Error fetching metric data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [
        props.metricSpecifiers,
        props.formulas,
        props.metoroQLQueries,
        props.reduceOption,
        timeRange,
        props.runtimeVariables,
        props.grids,
        props.widgetId,
        environments,
        props.startTime,
        props.endTime
    ]);

    useEffect(() => {
        // If there are multiple stat values then this is table
        if (statValues.length != 1) {
            setSingleValueToRender("value");
            setIsTable(true);
            props.setIsTable && props.setIsTable(true);
            return
        }

        // Otherwise there is one stat value
        let value = statValues[0]

        if (value.attributes === undefined || Object.keys(value.attributes).length === 0) {
            // If there are no attributes, we can not display this as a table
            setSingleValueToRender("value");
            setIsTable(false);
            props.setIsTable && props.setIsTable(false);
            return;
        }

        if (props.isSelecting) {
            // If we are in selecting mode, we should render as a table
            setSingleValueToRender("value");
            setIsTable(true);
            props.setIsTable && props.setIsTable(true);
            return;
        }

        // Are all the attributes and value hidden?
        let allHiddenApartFromOne = false;
        if (props.columnConfigs && value.attributes) {
            let valueHidden = false;
            let numHidden = 0;
            for (let j = 0; j < props.columnConfigs.length; j++) {
                const columnConfig = props.columnConfigs[j];
                if (columnConfig.hiddenByDefault) {
                    numHidden++;
                    if (columnConfig.id === "value") {
                        valueHidden = true;
                    }
                }
            }
            let numAttributes = Object.keys(value.attributes).length;
            if (numHidden === numAttributes && valueHidden) {
                allHiddenApartFromOne = true;
                // get the attribute to display
                for (let j = 0; j < props.columnConfigs.length; j++) {
                    const columnConfig = props.columnConfigs[j];
                    if (!columnConfig.hiddenByDefault) {
                        setSingleValueToRender(columnConfig.id.replace("attributes.", ""));
                        break
                    }
                }
            }
        }

        if (allHiddenApartFromOne) {
            setIsTable(false);
            props.setIsTable && props.setIsTable(false);
        } else {
            setSingleValueToRender("value");
            setIsTable(true);
            props.setIsTable && props.setIsTable(true);
        }
    }, [statValues]);

    if (isLoading) {
        return (
            <div className={cn("w-full h-full flex items-center justify-center", props.className)}>
                <LoadingSpinner className="h-6 w-6"/>
            </div>
        );
    }

    if (!isTable) {
        if (singleValueToRender === "value") {
            let value = (statValues.length > 0 && statValues[0]?.value) || 0;
            const unit = (statValues.length > 0 && statValues[0]?.unit) || metricUnit;

            return (
                <div className={cn("w-full h-full flex items-center justify-center p-1", props.className)}>
                    <Tooltip>
                        <TooltipTrigger className={cn("font-bold w-full h-full")}
                                        style={getVisualStyles(value)}>
                            {(!props.fontSize || props.fontSize == "auto") &&
                                <Textfit className={"h-full p-2 flex flex-col items-center justify-center"}
                                         forceSingleModeWidth={false}
                                         mode={"single"}>
                                    {formatValue(value, unit)}
                                </Textfit>}
                            {
                                props.fontSize && props.fontSize !== 'auto' && <div
                                    className={cn("h-full p-2 flex flex-col items-center justify-center truncate", props.fontSize)}>{formatValue(value, unit)}</div>
                            }
                        </TooltipTrigger>
                        <TooltipContent>
                            {formatValue(value, unit)}
                        </TooltipContent>
                    </Tooltip>
                </div>
            );
        } else {
            // If we have a single attribute to display, show it
            const attributeValue = statValues[0]?.attributes[singleValueToRender] || '-';
            return (
                <div className={cn("w-full h-full flex items-center justify-center p-1", props.className)}>
                    <Tooltip>
                        <TooltipTrigger className={cn("font-bold w-full h-full",)}>
                            {(!props.fontSize || props.fontSize == "auto") &&
                                <Textfit className={"h-full p-2 flex flex-col items-center justify-center truncate"}
                                         forceSingleModeWidth={false}
                                         mode={"single"}>
                                    {attributeValue}
                                </Textfit>}
                            {
                                props.fontSize && props.fontSize !== 'auto' && <div
                                    className={cn("h-full p-2 flex flex-col items-center justify-center truncate", props.fontSize)}>{attributeValue}</div>
                            }
                        </TooltipTrigger>
                        <TooltipContent>
                            {attributeValue}
                        </TooltipContent>
                    </Tooltip>
                </div>
            );
        }
    }

    // If we have multiple values, display them in a DataTable
    const allAttributes = new Set<string>();
    statValues.forEach(value => {
        Object.keys(value.attributes).forEach(key => allAttributes.add(key));
    });
    const attributeKeys = Array.from(allAttributes);


    // Create columns for the DataTable
    const columns: ColumnDef<StatTableRow>[] = [
        ...attributeKeys.map((key) => ({
            // You can not change this id as it will break columnConfigs
            id: `attributes.${key}`,
            accessorKey: `attributes.${key}`,
            header: ({column}: { column: Column<StatTableRow> }) => (
                // @ts-ignore
                <DataTableColumnHeader column={column}
                    // @ts-ignore
                                       title={column.columnDef.meta && column.columnDef.meta.title && column.columnDef.meta.title.length > 0 ? column.columnDef.meta.title : key}
                />

            ),
            cell: ({row}: { row: { original: StatTableRow } }) => {
                const text = row.original.attributes[key] || '-';
                if (props.columnConfigs) {
                    const columnConfig = props.columnConfigs.find((config: ColumnConfig) => config.id === 'attributes.' + key);
                    if (columnConfig && columnConfig.valueRegexExtractor) {
                        try {
                            const regex = new RegExp(columnConfig.valueRegexExtractor);
                            const match = text.match(regex);
                            if (match) {
                                return <div className="text-textmedium">{match[0]}</div>;
                            }
                        } catch (error) {
                            // Handle invalid regex gracefully
                            // if (Date.now() - lastRenderErrorTime > 5000) {
                            //     lastRenderErrorTime = Date.now();
                            //     toast({
                            //         title: "Error",
                            //         description: `Invalid regex pattern in column "${key}": ${columnConfig.valueRegexExtractor}`,
                            //         variant: "destructive",
                            //         duration: 5000
                            //     })
                            // }
                        }
                    }
                }
                return <div className="text-textmedium">{text}</div>;
            },
            // @ts-ignore
            sortingFn: (rowA, rowB) => {
                let row1Text = rowA.original.attributes[key] || '';
                let row2Text = rowB.original.attributes[key] || '';
                if (props.columnConfigs) {
                    const columnConfig = props.columnConfigs.find((config: ColumnConfig) => config.id === 'attributes.' + key);
                    if (columnConfig && columnConfig.valueRegexExtractor) {
                        try {
                            const regex = new RegExp(columnConfig.valueRegexExtractor);
                            const match = row1Text.match(regex);
                            if (match) {
                                row1Text = match[0];
                            }
                            const match2 = row2Text.match(regex);
                            if (match2) {
                                row2Text = match2[0];
                            }
                        } catch (error) {
                            // Handle invalid regex gracefully
                            console.warn("Invalid regex pattern in sorting function:", columnConfig.valueRegexExtractor);
                        }
                    }
                }
                return row1Text.localeCompare(row2Text);
            },
            enableSorting: true,
            meta: {
                title: key
            },
        })),
        {
            id: "value",
            accessorKey: "value",
            header: ({column}: { column: Column<StatTableRow> }) => (
                <DataTableColumnHeader column={column} title="Value"/>
            ),
            cell: ({row}: { row: { original: StatTableRow } }) => {
                const value = row.original.value;
                const unit = row.original.unit || metricUnit;
                return (
                    <div style={getVisualStyles(value)} className="text-textmedium">
                        {formatValue(value, unit)}
                    </div>
                );
            },
            enableSorting: true,
            // @ts-ignore
            sort: "desc"
        },
    ];


    // Create rows for the DataTable
    const rows: StatTableRow[] = statValues.map((value, index) => ({
        ...value,
        id: index.toString(),
    }));

    return (
        <div className={cn("w-full h-full flex flex-col", props.className)}>
            <DataTable
                isEditable={props.isEditable}
                columnConfigs={props.columnConfigs}
                setColumnConfigs={props.setColumnConfigs}
                columns={columns}
                data={rows}
                showPagination={rows.length > 10}
                filters={[]}
                isLoading={isLoading}
                tableTitle={props.title}
                setTitle={props.setTitle}
                removeBorder={props.removeBorder}
            />
        </div>
    );
}
